import type { ReactNode, RefObject } from "react";
import { useEffect, useMemo } from "react";
import type {
  SingleValue,
  Props as SelectProps,
  GroupBase,
} from "react-select";
import FormField from "./FormField";
import type { Option } from "Models";
import { customStyles } from "utils/ui/uiHelpers";
import { usePortalDropdown } from "utils/ui/usePortalDropdown";
import { Controller, useFormContext } from "react-hook-form";
import type { RegisterOptions, SetValueConfig } from "react-hook-form";
import { cx } from "@/utils/ui/uiHelpers";
import Select from "react-select";

type SelectFormFieldProps<T> = {
  label: string;
  icon?: ReactNode;
  className?: string;
  fieldName: string;
  defaultMissingMessage?: string;
  options: Option<T>[];
  rules?: RegisterOptions;
  tooltip?: string;
  defaultValue?: T;
  value?: T;
  showError?: boolean;
  /** Callback for side effects after value changes. Controller always owns state. */
  onValueChange?: (value: T) => void;
  /** Options passed to setValue. Use to control shouldValidate, shouldDirty, shouldTouch. */
  setValueOptions?: SetValueConfig;
} & Omit<
  SelectProps<Option<T>, false, GroupBase<Option<T>>>,
  "options" | "value" | "onChange" | "onBlur" | "name" | "ref" | "defaultValue"
>;

function SelectFormField<T>({
  // FormField props
  label,
  icon,
  className,
  fieldName,
  tooltip,
  defaultMissingMessage,
  showError = true,
  // Form control
  options,
  rules,
  defaultValue,
  // Controlled overrides
  value: valueProp,
  onValueChange,
  setValueOptions,
  // Select props with defaults
  menuPlacement = "auto",
  isDisabled = false,
  // Rest goes to Select
  ...selectProps
}: SelectFormFieldProps<T>): ReactNode {
  const dropdown = usePortalDropdown();

  const { control, setValue, getValues } = useFormContext();

  const mergedRules = useMemo(() => {
    const baseRules = rules || {};

    if (defaultMissingMessage) {
      return {
        ...baseRules,
        required: baseRules.required || defaultMissingMessage,
      };
    }

    return baseRules;
  }, [rules, defaultMissingMessage]);

  // Set default value if field is empty - moved outside render prop
  useEffect(() => {
    const currentValue = getValues(fieldName);
    if (currentValue == null && defaultValue !== undefined) {
      setValue(fieldName, defaultValue);
    }
  }, [fieldName, defaultValue, setValue, getValues]);

  return (
    <FormField
      label={label}
      icon={icon}
      className={className}
      fieldName={fieldName}
      defaultMissingMessage={defaultMissingMessage}
      tooltip={tooltip}
      showError={showError}
    >
      <Controller
        name={fieldName}
        control={control}
        rules={mergedRules}
        defaultValue={defaultValue}
        render={({ field }) => {
          // Use props if provided, otherwise use field
          const value = valueProp ?? field.value;
          const handleChange = (selected: SingleValue<Option<T>>) => {
            if (selected) {
              if (setValueOptions) {
                setValue(fieldName, selected.value, setValueOptions);
              } else {
                field.onChange(selected.value);
              }
              onValueChange?.(selected.value);
            }
          };

          return (
            <div ref={dropdown.triggerRef as RefObject<HTMLDivElement>}>
              <Select<Option<T>, false, GroupBase<Option<T>>>
                options={options}
                value={options.find((option) => option.value === value) ?? null}
                onChange={handleChange}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                styles={dropdown.getReactSelectStyles(customStyles)}
                className={cx(
                  "basic-single",
                  isDisabled &&
                    "disabled:cursor-not-allowed disabled:opacity-50",
                )}
                classNamePrefix="select"
                menuPlacement={menuPlacement}
                isDisabled={isDisabled}
                {...dropdown.getReactSelectProps()}
                {...selectProps}
              />
            </div>
          );
        }}
      />
    </FormField>
  );
}

export default SelectFormField;
